import { useEffect, useState, useRef } from 'react';
import { ScrollReveal } from '@/components/ui/scroll-reveal';

interface StatItemProps {
  value: number;
  suffix: string;
  label: string;
  delay?: number;
}

function useCountUp(target: number, duration: number = 2000, shouldStart: boolean) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime: number | null = null;
    let animationFrame: number;
    
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * target));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration, shouldStart]);
  
  return count;
}

function StatItem({ value, suffix, label, delay = 0 }: StatItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = useCountUp(value, 2000, isVisible);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <ScrollReveal delay={delay}>
      <div ref={ref} className="text-center">
        <div className="text-5xl sm:text-6xl lg:text-7xl font-display font-semibold text-foreground mb-2">
          {count}{suffix}
        </div>
        <h3 className="text-headline-3 text-foreground">
          {label}
        </h3>
      </div>
    </ScrollReveal>
  );
}

export function StatsSection() {
  const stats = [
    { value: 15, suffix: '+', label: 'Artists' },
    { value: 349, suffix: '+', label: 'Classes' },
    { value: 129, suffix: '+', label: 'Bonus Content' },
  ];

  return (
    <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 sm:gap-8">
          {stats.map((stat, index) => (
            <StatItem
              key={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              label={stat.label}
              delay={index * 0.15}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
